/**
 * Domain constants carried over from the legacy LeadGENOS SPA + Apps Script backend, so the
 * rewrite stays field-for-field compatible with existing exports and any migrated Sheets data.
 *
 * NOTE: the former US/Wing "product" grouping has been removed — LeadGENOS is now a single
 * shared pool of leads/campaigns/activity, and the only distinction between users is their ROLE.
 */
/**
 * Roles.
 *  - ADMIN      manages users AND still counts as a rep (can own leads / show in reports).
 *  - ADMIN_ONLY full admin rights but is NEVER a rep: never assigned leads, never listed in any
 *               rep dropdown, and never visible in any performance report. A pure "manage the
 *               system" account.
 *  - MANAGER    full data access across all reps.
 *  - REP        scoped to their own leads.
 */
export const ROLES = ['ADMIN', 'ADMIN_ONLY', 'MANAGER', 'REP'];
/** Human-friendly labels for role <select>s and badges (the raw enum values read poorly). */
export const ROLE_LABEL = {
    ADMIN: 'Admin',
    ADMIN_ONLY: 'Admin Only',
    MANAGER: 'Manager',
    REP: 'Rep',
};
/** Roles with administrative privileges (user & content management). */
export const ADMIN_ROLES = ['ADMIN', 'ADMIN_ONLY'];
/** Roles with manager-level data access (all reps' data, reports, "View as Rep"). */
export const MANAGER_ROLES = ['ADMIN', 'ADMIN_ONLY', 'MANAGER'];
export const isAdminRole = (role) => role === 'ADMIN' || role === 'ADMIN_ONLY';
export const isManagerRole = (role) => role === 'MANAGER' || isAdminRole(role);
/**
 * Whether a user may own leads and appear in rep dropdowns / performance reports. ADMIN_ONLY is
 * the pure-management role and is excluded everywhere a "rep" is listed, assigned, or reported.
 */
export const isRepAssignable = (role) => role !== 'ADMIN_ONLY';
/**
 * Team groups. This is an ADMIN-only organizational label for assigning members — it does NOT
 * scope leads/campaigns/dashboards (those remain one shared pool). Regular users never see it.
 */
export const GROUPS = ['CGM', 'WING'];
export const GROUP_LABEL = {
    CGM: 'CGM',
    WING: 'Wing',
};
/**
 * The 48 "Master Leads" column headers, in order, from the legacy `setupTabs()` HEADERS
 * array. Used by import/export to speak the exact same shape as the old Sheets backend.
 */
export const LEAD_HEADERS = [
    'Company Name',
    'Number of Physicians',
    'Main Specialty',
    'Contact First Name',
    'Contact Last Name',
    'Title',
    'Contact Email',
    'Phone',
    'Website',
    'LinkedIn',
    'ID',
    'Address',
    'City',
    'State',
    'Zip Code',
    'Campaign Name',
    'Campaign Start Date',
    'Rep Name',
    '1st Call',
    '1O',
    '2nd Call',
    '2O',
    '3rd Call',
    '3O',
    '4th Call',
    '4O',
    '5th Call',
    '5O',
    '6th Call',
    '6O',
    'Lead Date',
    'Qual/Disqualification Reason',
    'Notes',
    'Notes2',
    'Notes3',
    'Notes4',
    'Notes5',
    'Notes6',
    'Current Vendor PM',
    'Current Vendor EHR',
    'Current Vendor LIS',
    'Current Billing',
    'Practice Type',
    'Is CGM Customer?',
    'Status',
    'Readiness',
    'LastTouch',
    'FollowUp',
];
/** The 6 call-attempt date headers and their outcome-code counterparts. */
export const CALL_HEADS = [
    '1st Call',
    '1O',
    '2nd Call',
    '2O',
    '3rd Call',
    '3O',
    '4th Call',
    '4O',
    '5th Call',
    '5O',
    '6th Call',
    '6O',
];
export const NOTE_HEADS = ['Notes', 'Notes2', 'Notes3', 'Notes4', 'Notes5', 'Notes6'];
/**
 * Activity fields (adjusted daily totals), keyed as the legacy `ACT_FIELD_MAP` did.
 *
 * `ibqls`/`obqls`/`convopps`/`wins` were added to feed the Team Leaderboard report: the
 * inbound/outbound qualified-lead split, converted opportunities, and closed-won deals that
 * the legacy single `qls` count could not express.
 */
export const ACTIVITY_FIELDS = [
    'dials',
    'emails',
    'qls',
    'ibqls',
    'obqls',
    'convopps',
    'wins',
    'webreg',
    'webinv',
    'noanswer',
    'vm',
    'lmwp',
    'callback',
    'notint',
    'remove',
    'badphone',
];
/** Human labels for the Activity columns as they appeared in the Sheet. */
export const ACTIVITY_LABELS = {
    dials: 'Dials',
    emails: 'Emails',
    qls: 'QLs',
    ibqls: 'Inbound QLs',
    obqls: 'Outbound QLs',
    convopps: 'Converted Opps',
    wins: 'Wins',
    webreg: 'Webinar Registered',
    webinv: 'Webinar Invite',
    noanswer: 'No Answer',
    vm: 'Voicemail',
    lmwp: 'LMWP',
    callback: 'Callback',
    notint: 'Not Interested',
    remove: 'Remove From Calling',
    badphone: 'Bad Phone',
};
/**
 * Team Leaderboard efficiency-score weights + coaching thresholds — the defaults mirror the
 * hidden "Settings" sheet of the source workbook. Admins/Managers can tune them (they are
 * stored in the `leaderboard` Setting and echoed back on the report).
 */
export const LEADERBOARD_WEIGHTS = {
    opps: 0.45, // Total converted opps (SQLs)
    conv: 0.3, // Conversion %
    obql: 0.2, // Outbound QLs
    activity: 0.05, // Dials (normalized activity)
};
export const LEADERBOARD_THRESHOLDS = {
    highActivityPct: 0.7,
    lowActivityPct: 0.3,
    highConvPct: 0.7,
    lowConvPct: 0.3,
    decliningTrend: -0.1, // 3-wk direction below this = declining
    strongGrowth: 0.15, // 3-wk direction above this = strong growth
};
/** The per-rep metrics shown as rows in the 8-week trend, in display order. */
export const LEADERBOARD_TREND_METRICS = [
    { key: 'dials', label: 'Dials' },
    { key: 'emails', label: 'Emails' },
    { key: 'webreg', label: 'Registered Webinar' },
    { key: 'ibqls', label: 'Inbound QLs' },
    { key: 'obqls', label: 'Outbound QLs' },
    { key: 'convopps', label: 'Converted Opps' },
    { key: 'wins', label: 'Wins' },
];
/** Call outcome codes seen in the legacy `OUTCOMES` map. */
export const OUTCOME_CODES = [
    'NA',
    'LMWP',
    'VM',
    'ES',
    'CB',
    'NI',
    'LI',
    'WI',
    'WR',
    'OSV',
    'DQ',
    'BPN',
    'MS',
];
/**
 * Lead pipeline statuses — the vocabulary from the previous LeadGENOS version (the Lead Status
 * Funnel: New → Working → Nurturing → Qualified → Meeting Set → Converted, plus Disqualified).
 * Stored as free text in the DB for forward-compat; listed here for the UI. Legacy values from
 * older rows/imports (QL, SQL, Attempting, …) are folded in via LEGACY_STATUS_MAP.
 */
export const STATUSES = [
    'New',
    'Working',
    'Nurturing',
    'Qualified',
    'Meeting Set',
    'Converted',
    'Won',
    'Lost',
    'Disqualified',
];
/**
 * The built-in statuses that carry hard-coded business semantics (funnel, KPIs, callable queue,
 * retention). The admin status editor may reorder or hide these and add custom statuses on top of
 * them, but it cannot rename or delete them — the reports/queue logic keys off these exact names.
 */
export const CORE_STATUSES = STATUSES;
/** The ordered pipeline stages shown in the Dashboard Lead Status Funnel (Won is the closed-won
 *  finish; Lost/Disqualified are terminal branches, not linear funnel stages). */
export const STATUS_FUNNEL = [
    'New',
    'Working',
    'Nurturing',
    'Qualified',
    'Meeting Set',
    'Converted',
    'Won',
];
/**
 * Map legacy/free-text statuses onto the current vocabulary so old rows and imports still resolve
 * in the funnel, filters, and counters. Anything not listed falls through unchanged.
 */
export const LEGACY_STATUS_MAP = {
    Attempting: 'Working',
    Callback: 'Working',
    QL: 'Qualified',
    SQL: 'Converted',
    Nurture: 'Nurturing',
    'Not Interested': 'Disqualified',
    'Bad Data': 'Disqualified',
    'Do Not Call': 'Disqualified',
};
/**
 * Stored statuses (current + legacy) that all mean "disqualified". Used by the retention sweep to
 * find leads eligible for automatic deletion, and by the backfill that stamps their disqualified
 * clock. Kept in sync with the 'Disqualified' entries in LEGACY_STATUS_MAP.
 */
export const DISQUALIFIED_STATUSES = [
    'Disqualified',
    'Not Interested',
    'Bad Data',
    'Do Not Call',
    'Lost',
];
/**
 * How long a lead is kept after it becomes Disqualified before the retention sweep deletes it.
 * Overridable at runtime via the DISQUALIFIED_RETENTION_DAYS env var (see services/retention).
 */
export const DISQUALIFIED_RETENTION_DAYS = 7;
/**
 * Statuses that count as "Qualified Lead or better". Superset of the current + legacy vocab so the
 * dashboard/leaderboard keep counting migrated rows (QL/SQL) alongside new ones.
 */
export const QL_PLUS = ['Qualified', 'Meeting Set', 'Converted', 'Won', 'QL', 'SQL'];
/** Statuses that represent a booked meeting or beyond (feeds the "Meetings Booked" KPI). */
export const MEETING_STATUSES = ['Meeting Set', 'Converted', 'Won'];
export const DISPOSITIONS = [
    { code: 'NA', label: 'No Answer', tone: 'neutral', setStatus: 'Working' },
    { code: 'LMWP', label: 'Left Msg w/ Person', tone: 'neutral', setStatus: 'Working' },
    { code: 'VM', label: 'Voicemail', tone: 'neutral', setStatus: 'Working' },
    { code: 'ES', label: 'Email Sent', tone: 'neutral', setStatus: 'Working' },
    { code: 'CB', label: 'Callback Set', tone: 'good', setStatus: 'Working' },
    { code: 'NI', label: 'Not Interested', tone: 'bad', terminal: true, setStatus: 'Disqualified' },
    { code: 'WI', label: 'Webinar Invite', tone: 'neutral', setStatus: 'Working' },
    { code: 'WR', label: 'Webinar Registered', tone: 'good', setStatus: 'Nurturing' },
    { code: 'OSV', label: 'On-Site Visit', tone: 'good', setStatus: 'Nurturing' },
    { code: 'DQ', label: 'Disqualified', tone: 'bad', terminal: true, setStatus: 'Disqualified' },
    { code: 'BPN', label: 'Bad Phone #', tone: 'warn', terminal: true },
    { code: 'DNC', label: 'Do Not Call', tone: 'bad', terminal: true, setStatus: 'Disqualified' },
];
/** Quick lookup: outcome code → human label (covers legacy codes too). */
export const OUTCOME_LABELS = {
    ...Object.fromEntries(DISPOSITIONS.map((d) => [d.code, d.label])),
    LI: 'LinkedIn Outreach',
    MS: 'Meeting Set (QL)',
};
// ---- Qualification form option lists --------------------------------------------------------
export const PAIN_POINTS = [
    'Aging A/R',
    'High denial rate',
    'Staffing constraints',
    'Manual workflow',
    'EHR frustration',
    'Slow lab turnaround',
    'Revenue leakage',
    'Compliance burden',
    'Other',
];
export const TIMELINE_OPTIONS = ['0–3 months', '3–6 months', '6–12 months', 'No timeline'];
export const BUDGET_OPTIONS = [
    'Confirmed',
    'Exploring',
    'Performance-based OK',
    'Unknown',
];
export const SPECIALTY_TYPES = [
    'Primary Care',
    'Cardiology',
    'Endocrinology',
    'Nephrology',
    'FQHC/RHC/CHC',
    'Laboratory',
    'Multi-Specialty',
    'Other',
];
/** Persona list for the Scripts page selector. */
export const PERSONAS = [
    'Office Manager',
    'Practice Owner',
    'Billing Manager',
    'CFO / Revenue-Cycle Leader',
    'Lab Director',
    'Administrator',
];
// ---- Time zones / callable window -----------------------------------------------------------
/** The callable business-hours window, in each lead's local time. */
export const CALLABLE_START_HOUR = 8; // 8:00 AM local
export const CALLABLE_END_HOUR = 18; // 6:00 PM local
/** Time-zone buckets shown in the Dashboard "Queue by Time Zone" strip, in display order. */
export const TZ_BUCKETS = [
    { key: 'ET', label: 'ET', tz: 'America/New_York' },
    { key: 'CT', label: 'CT', tz: 'America/Chicago' },
    { key: 'MT', label: 'MT', tz: 'America/Denver' },
    { key: 'PT', label: 'PT', tz: 'America/Los_Angeles' },
    { key: 'AK', label: 'AK', tz: 'America/Anchorage' },
    { key: 'HI', label: 'HI', tz: 'Pacific/Honolulu' },
];
/** US state / territory (2-letter) → time-zone bucket key. Defaults to ET when unknown. */
export const STATE_TZ = {
    // Eastern
    CT: 'ET', DE: 'ET', DC: 'ET', FL: 'ET', GA: 'ET', IN: 'ET', ME: 'ET', MD: 'ET', MA: 'ET',
    MI: 'ET', NH: 'ET', NJ: 'ET', NY: 'ET', NC: 'ET', OH: 'ET', PA: 'ET', RI: 'ET', SC: 'ET',
    VT: 'ET', VA: 'ET', WV: 'ET',
    // Central
    AL: 'CT', AR: 'CT', IL: 'CT', IA: 'CT', KS: 'CT', KY: 'CT', LA: 'CT', MN: 'CT', MS: 'CT',
    MO: 'CT', NE: 'CT', ND: 'CT', OK: 'CT', SD: 'CT', TN: 'CT', TX: 'CT', WI: 'CT',
    // Mountain
    AZ: 'MT', CO: 'MT', ID: 'MT', MT: 'MT', NM: 'MT', UT: 'MT', WY: 'MT', NV: 'PT',
    // Pacific
    CA: 'PT', OR: 'PT', WA: 'PT',
    // Alaska / Hawaii + Pacific territories
    AK: 'AK', HI: 'HI', GU: 'HI', PR: 'ET', VI: 'ET',
};
/**
 * Single shared campaign list — the union of the former US and Wing lists (deduped), with the
 * shared "Other" bucket kept last. Editable by managers/admins in Admin → Campaigns.
 */
export const CAMPAIGNS = [
    'ARIA RCM & Billing',
    'Aged AR',
    'Credentialing / Enrollment',
    'Customer Base Campaigns',
    'ARIA RPM',
    'APRIMA EHR/PM',
    'CGM LIS/LIM — Net New',
    'New CMS NPI',
    'CLIA Waiver Lab',
    'RSE/FSE On Site',
    'User Conference',
    'Webinar Recruitment',
    'Other',
];
/**
 * Seed roster — everyone in one shared pool. Lisa is seeded as ADMIN; the rest as REP. The
 * `group` is just the admin-facing membership label (former CGM/US people → CGM, former Wing
 * people → WING). Roles and groups are changed afterward in Admin → Users & Roles.
 */
export const SEED_REPS = [
    { repKey: 'lisa', name: 'Lisa Clark', role: 'ADMIN', group: 'CGM' },
    { repKey: 'brenda', name: 'Brenda Howard', role: 'REP', group: 'CGM' },
    { repKey: 'hanna', name: 'Hanna Halum', role: 'REP', group: 'CGM' },
    { repKey: 'maia', name: 'Maia Borham', role: 'REP', group: 'CGM' },
    { repKey: 'ryan', name: 'Ryan Tesorero', role: 'REP', group: 'WING' },
    { repKey: 'ely', name: 'Ely Joy Dionela', role: 'REP', group: 'WING' },
    { repKey: 'paul', name: 'Paul Murillo', role: 'REP', group: 'WING' },
];
//# sourceMappingURL=constants.js.map